
import Vue from "vue";
import AppTimeago from "@/components/common/Timeago.vue";
import AppPdfViewer from "@/components/common/PdfViewer.vue";
import AppDelayedProgress from "@/components/common/DelayedProgress.vue";
import AppSetNoteForBookkeeperModal from "@/components/inbox/SetNoteForBookkeeperModal.vue";
import AppAssignLabelsModal from "@/components/inbox/AssignLabelsModal.vue";
import { AWSFileService } from "@/services";
import { InboxreaderService } from "@/services/inboxreader-service";
import { InboxState } from "@/utils/inbox/inbox-state";
import { selectionEntry } from "@/utils/inbox/inbox-list-state";
import { daysUntil } from "@/utils/inbox/dates";
import { getLabelTextColor } from "@/utils/inbox/label-color";
import { printLocalDate } from "@/utils/pretty-print";
import { ISelectItem } from "winfakt-vue-components";
import {
	InboxDocumentRow,
	documentRows,
	rowAmount,
	rowAmountExcl,
	rowDueDate,
	rowInvoiceDate,
	rowInvoiceNumber,
	rowReference,
	rowSource,
	rowSourceIcon,
	rowSupplier,
	rowVatNumber,
	rowVatPercentage
} from "@/utils/inbox/document-row";

export default Vue.extend({
	data(){
		return {
			activeKey: "",
			previewBlob: null as Blob | null,
			previewImageUrl: "",
			previewError: "",
			previewToken: "",
			noteOpen: false,
			labelsOpen: false,
			creating: false
		};
	},
	props: {
		state: {
			type: Object as ()=>InboxState,
			required: true
		}
	},
	computed: {
		dash():string{
			return "—";
		},
		rows():InboxDocumentRow[]{
			return documentRows(this.state.listState.purchases, this.state.archive);
		},
		activeRow():InboxDocumentRow | null{
			return this.rows.find(r=>r.key == this.activeKey) || null;
		}
	},
	watch: {
		rows: {
			immediate: true,
			handler(){
				// Keep a document open while the list refreshes; open the first
				// one when what was open is gone.
				if (this.activeRow) return;
				let first = this.rows[0];
				if (!first) {
					this.activeKey = "";
					this.clearPreview();
					return;
				}
				this.openRow(first);
			}
		}
	},
	methods: {
		source(row:InboxDocumentRow):string{
			return rowSource(row.purchase);
		},
		sourceIcon(row:InboxDocumentRow):string | string[]{
			return rowSourceIcon(row.purchase);
		},
		supplier(row:InboxDocumentRow):string{
			return rowSupplier(row);
		},
		reference(row:InboxDocumentRow):string{
			return rowReference(row);
		},
		invoiceNumber(row:InboxDocumentRow):string{
			return rowInvoiceNumber(row);
		},
		amount(row:InboxDocumentRow):string{
			return rowAmount(row);
		},
		amountExcl(row:InboxDocumentRow):string{
			return rowAmountExcl(row);
		},
		vatPercentage(row:InboxDocumentRow):string{
			return rowVatPercentage(row);
		},
		vatNumber(row:InboxDocumentRow):string{
			return rowVatNumber(row);
		},
		invoiceDate(row:InboxDocumentRow):Date | null{
			return rowInvoiceDate(row);
		},
		isApproved(row:InboxDocumentRow):boolean{
			return row.file.Approved;
		},
		textColor(color:string):string{
			return getLabelTextColor(color);
		},
		printDate(date:Date | null):string{
			if (!date) return this.dash;
			return printLocalDate(date);
		},
		// dateInfo is the one date that matters most on a row: the due date when
		// there is one, otherwise the date of the document itself.
		dateInfo(row:InboxDocumentRow):string{
			let due = rowDueDate(row);
			if (due) {
				return `${this.$ct("expires-short")} ${printLocalDate(due)}`;
			}
			let invoiceDate = rowInvoiceDate(row);
			if (invoiceDate) return `${this.$ct("invoice-date-short")} ${printLocalDate(invoiceDate)}`;
			return "";
		},
		// A due date that has passed gets no remark: whether the invoice was
		// paid is not something the inbox knows.
		dueDateWithDays(row:InboxDocumentRow):string{
			let due = rowDueDate(row);
			if (!due) return this.dash;
			let days = daysUntil(due);
			if (days == null || days < 0) return printLocalDate(due);
			return `${printLocalDate(due)} (${this.$ct("in-days", {count: days})})`;
		},
		receivedVia(row:InboxDocumentRow):string{
			let source = rowSource(row.purchase);
			if (source == "peppol") return this.$ct("received-via-peppol").toString();
			if (source == "upload") return this.$ct("received-via-upload").toString();
			return this.$ct("received-via-email").toString();
		},
		openRow(row:InboxDocumentRow){
			this.activeKey = row.key;
			this.loadPreview(row);
		},
		clearPreview(){
			this.previewBlob = null;
			this.previewImageUrl = "";
			this.previewError = "";
			this.previewToken = "";
		},
		// loadPreview fetches the document itself: the thumbnail is too small to
		// read. The token guards against a slow fetch of a document the user
		// already clicked away from.
		async loadPreview(row:InboxDocumentRow){
			this.clearPreview();
			let file = row.file.File;
			if (!file) {
				this.previewError = this.$ct("preview-not-available").toString();
				return;
			}
			let token = row.key;
			this.previewToken = token;
			if (file.ContentType != "application/pdf") {
				if (file.ContentType.indexOf("image/") != 0) {
					this.previewError = this.$ct("preview-not-available").toString();
					return;
				}
				this.previewImageUrl = file.getUrl();
				return;
			}
			try{
				let fetched = await AWSFileService.fetchFile(file.Token, file.Name, file.ContentType);
				if (this.previewToken != token) return;
				this.previewBlob = fetched;
			}catch(err){
				if (this.previewToken != token) return;
				console.error(err);
				this.previewError = this.$ct("preview-not-available").toString();
			}
		},
		async download(row:InboxDocumentRow){
			let file = row.file.File;
			if (!file) return;
			await AWSFileService.downloadFile(file.Token, file.Name);
		},
		menuItems(row:InboxDocumentRow):ISelectItem<string>[]{
			let items:ISelectItem<string>[] = [
				{text: this.$ct("add-label").toString(), icon: "tag", value: "labels"},
				{text: this.$ct("set-note-for-bookkeeper").toString(), icon: "sticky-note", value: "note"}
			];
			if (rowSource(row.purchase) == "email") {
				items.push({text: this.$ct("open-email").toString(), icon: "envelope", value: "open-mail"});
			}
			items.push({text: this.$ct("forward-dots").toString(), icon: "share", value: "send-mail", devider: true});
			if (this.state.archive) {
				items.push({text: this.$ct("unarchive-document").toString(), icon: "undo", value: "unarchive"});
			} else {
				items.push({text: this.$ct("archive-document").toString(), icon: "archive", value: "archive"});
			}
			if (!row.purchase.PeppolOrder) {
				items.push({text: this.$ct("delete-document").toString(), icon: "trash", value: "delete"});
			}
			return items;
		},
		async onMenuClick(row:InboxDocumentRow, action:string){
			if (action == "labels") {
				this.labelsOpen = true;
				return;
			}
			if (action == "note") {
				this.noteOpen = true;
				return;
			}
			if (action == "open-mail") {
				this.state.preview = row.purchase;
				return;
			}
			if (action == "send-mail") {
				this.$emit("send-mail", row.purchase);
				return;
			}
			if (action == "archive") {
				await this.archive(row);
				return;
			}
			if (action == "unarchive") {
				await this.unarchive(row);
				return;
			}
			if (action == "delete") {
				await this.deleteRow(row);
			}
		},
		async unarchive(row:InboxDocumentRow){
			if (row.purchase.PeppolOrder) {
				await InboxreaderService.unarchiveInboxreaderPurchase(row.purchase.ID);
			} else {
				await InboxreaderService.unarchiveInboxreaderPurchaseFile(row.purchase.ID, row.file.ID);
			}
			this.activeKey = "";
			this.$emit("refresh");
		},
		async deleteRow(row:InboxDocumentRow){
			let message = this.$ct("are-you-sure-you-want-to-delete-this-document");
			let confirm = await this.$wf.confirm(message, this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			await InboxreaderService.deleteInboxreaderPurchaseFile(row.purchase.ID, row.file.ID);
			this.activeKey = "";
			this.$emit("refresh");
		},
		async archive(row:InboxDocumentRow){
			let message = this.$ct("are-you-sure-you-want-to-archive-this-document");
			let confirm = await this.$wf.confirm(message, this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			// A peppol document is archived as a whole, of a mail only the file
			// of this row.
			if (row.purchase.PeppolOrder) {
				await InboxreaderService.archiveInboxreaderPurchase(row.purchase.ID);
			} else {
				await InboxreaderService.archiveInboxreaderPurchaseFile(row.purchase.ID, row.file.ID);
			}
			this.activeKey = "";
			this.$emit("refresh");
		},
		// create hands the document to the selection the rest of the inbox works
		// with, so the purchase is made in exactly the same way as elsewhere.
		create(row:InboxDocumentRow){
			this.creating = true;
			let selection = this.state.listState.selection;
			selection.splice(0, selection.length, selectionEntry(row.purchase, row.file));
			this.$emit("create");
			this.creating = false;
		},
		onScroll(){
			let el = this.$refs.listEl as HTMLElement;
			if (!el) return;
			if (el.scrollHeight - el.clientHeight - el.scrollTop > 300) return;
			this.state.fetchMore();
		}
	},
	components: {
		AppTimeago,
		AppPdfViewer,
		AppDelayedProgress,
		AppSetNoteForBookkeeperModal,
		AppAssignLabelsModal
	},
	i18nEx: {
		componentPrefix: "components.purchases"
	}
});
